import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { bootstrap, login as apiLogin, logout as apiLogout, verifyMfa as apiVerifyMfa, type Me } from './lib/api'

interface AuthState {
  me: Me | null
  loading: boolean
  error: string | null
  signIn: (email: string, password: string) => Promise<'ok' | 'mfa'>
  confirmMfa: (code: string) => Promise<void>
  pendingMfa: string | null
  signOut: () => Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pendingMfa, setPendingMfa] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    bootstrap()
      .then((m) => { if (alive) setMe(m) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const signIn = useCallback(async (email: string, password: string): Promise<'ok' | 'mfa'> => {
    setError(null)
    try {
      const r = await apiLogin(email, password)
      if (r.mfa_required) {
        setPendingMfa(r.mfa_token)
        return 'mfa'
      }
      setMe({ user: r.user, tenant: r.tenant, role: r.role })
      return 'ok'
    } catch (e) {
      setError(e instanceof Error ? e.message : 'เข้าสู่ระบบไม่สำเร็จ')
      throw e
    }
  }, [])

  const confirmMfa = useCallback(async (code: string) => {
    if (!pendingMfa) return
    setError(null)
    try {
      const r = await apiVerifyMfa(pendingMfa, code)
      setMe({ user: r.user, tenant: r.tenant, role: r.role })
      setPendingMfa(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'รหัสไม่ถูกต้อง')
      throw e
    }
  }, [pendingMfa])

  const signOut = useCallback(async () => {
    await apiLogout()
    setMe(null)
    setPendingMfa(null)
  }, [])

  const value = useMemo(() => ({ me, loading, error, signIn, confirmMfa, pendingMfa, signOut }),
    [me, loading, error, signIn, confirmMfa, pendingMfa, signOut])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used inside AuthProvider')
  return v
}
